/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import dayjs from "dayjs";
import axios from "axios";

import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import InformationIcon from "../../assets/icons/info/information.png";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useParcels } from "../../store/useParcels";
import { useDropLocations } from "../../store/useDropLocations";
import {
  getParcelCourierColor,
  TOTAL_CONTRAST_COLOR,
} from "../../constants/plasticColors";

interface AveragePricePerParcelProps {
  filterType?: "location" | "city";
  filterPlaces?: string[];
  sequenceStep?: number;
  setSequenceStep?: (n: number) => void;
  sequenceStepIndex?: number;
}

export const AveragePricePerParcel = React.memo(
  ({
    filterType,
    filterPlaces,
    sequenceStep,
    setSequenceStep,
    sequenceStepIndex,
  }: AveragePricePerParcelProps) => {
    const useGeneralFilter =
      filterType !== undefined && filterPlaces !== undefined;
    const snapshotRef = useRef(null);
    const {
      averagePricePerParcelPerCourier,
      getAveragePricePerParcelPerCourier,
      isLoadingAveragePricePerParcel,
      isAveragePricePerParcelError,
    } = useParcels();
    const { dropLocations, getDropLocations } = useDropLocations();
    const [_, takeScreenshot] = useScreenshot();
    const [isCsvDownloading, setIsCsvDownloading] = useState(false);

    const [startYear, setStartYear] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.year();
    });
    const [startMonth, setStartMonth] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.month() + 1;
    });
    const [endYear, setEndYear] = useState(new Date().getFullYear());
    const [endMonth, setEndMonth] = useState(new Date().getMonth() + 1);
    const [period, setPeriod] = useState<"month" | "quarter" | "year">("month");
    const [startQuarter, setStartQuarter] = useState(1);
    const [endQuarter, setEndQuarter] = useState(4);

    const yearOptions = Array.from(
      { length: dayjs().year() - 2023 },
      (_, i) => dayjs().year() - i
    );

    const monthOptions = [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ];

    const startMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return startYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [startYear]);

    const endMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return endYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [endYear]);

    const quarterOptions = [
      { value: 1, label: "Q1" },
      { value: 2, label: "Q2" },
      { value: 3, label: "Q3" },
      { value: 4, label: "Q4" },
    ];

    const filteredData = useMemo(
      () => averagePricePerParcelPerCourier ?? [],
      [averagePricePerParcelPerCourier]
    );

    const transformedData = useMemo(() => {
      const raw = filteredData ?? [];
      if (!raw.length) return raw;

      if (period === "month") return raw;

      if (period === "year") {
        return raw.map((series) => {
          const sData =
            (series.data as unknown as [number, number][] | undefined) ?? [];
          const yearMap = new Map<number, { sum: number; count: number }>();
          sData.forEach(([ts, val]) => {
            const y = dayjs(ts).year();
            if (y >= startYear && y <= endYear) {
              const prev = yearMap.get(y) ?? { sum: 0, count: 0 };
              yearMap.set(y, {
                sum: prev.sum + val,
                count: prev.count + 1,
              });
            }
          });
          const data: [number, number][] = [];
          for (let y = startYear; y <= endYear; y++) {
            const v = yearMap.get(y);
            const avg =
              v && v.count > 0 ? Math.round((v.sum / v.count) * 100) / 100 : 0;
            data.push([new Date(y, 0, 1).getTime(), avg]);
          }
          return { ...series, data };
        });
      }

      if (period === "quarter") {
        return raw.map((series) => {
          const sData =
            (series.data as unknown as [number, number][] | undefined) ?? [];
          const quarterMap = new Map<
            string,
            { year: number; q: number; sum: number; count: number }
          >();
          sData.forEach(([ts, val]) => {
            const d = dayjs(ts);
            const year = d.year();
            const q = Math.ceil((d.month() + 1) / 3);
            const key = `${year}-${q}`;
            const prev = quarterMap.get(key) ?? {
              year,
              q,
              sum: 0,
              count: 0,
            };
            quarterMap.set(key, {
              ...prev,
              sum: prev.sum + val,
              count: prev.count + 1,
            });
          });
          const sorted = Array.from(quarterMap.values()).sort((a, b) =>
            a.year === b.year ? a.q - b.q : a.year - b.year
          );
          const data = sorted.map(({ year, q, sum, count }) => {
            const avg = count > 0 ? Math.round((sum / count) * 100) / 100 : 0;
            const monthIndex = (q - 1) * 3 + 1;
            return [new Date(year, monthIndex - 1, 15).getTime(), avg] as [
              number,
              number,
            ];
          });
          return { ...series, data };
        });
      }

      return raw;
    }, [filteredData, period, startYear, endYear]);

    const seriesNames = useMemo(
      () => (transformedData ?? []).map((s) => s?.name || ""),
      [transformedData]
    );
    const seriesColors = useMemo(
      () =>
        seriesNames.map((name) =>
          name === "Total all couriers"
            ? TOTAL_CONTRAST_COLOR
            : getParcelCourierColor(name)
        ),
      [seriesNames]
    );

    const monthlyTickAmount = useMemo(() => {
      const firstData =
        (transformedData?.[0]?.data as unknown as
          | [number, number][]
          | undefined) ?? [];
      return firstData.length ? firstData.length - 1 : 11;
    }, [transformedData]);

    useEffect(() => {
      getDropLocations();
    }, [getDropLocations]);

    const prevFilterSigRef = useRef<string | null>(null);

    useEffect(() => {
      if (
        sequenceStep !== undefined &&
        setSequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep < sequenceStepIndex
      ) {
        return;
      }
      const filterSig = JSON.stringify({
        period,
        startYear,
        startMonth,
        startQuarter,
        endYear,
        endMonth,
        endQuarter,
      });
      if (
        sequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep > sequenceStepIndex &&
        prevFilterSigRef.current === filterSig
      ) {
        return;
      }
      prevFilterSigRef.current = filterSig;

      let apiStartYear: number;
      let apiStartMonth: number;
      let apiEndYear: number;
      let apiEndMonth: number;

      if (period === "quarter") {
        apiStartYear = startYear;
        apiStartMonth = (startQuarter - 1) * 3 + 1;
        apiEndYear = endYear;
        apiEndMonth = endQuarter * 3;
      } else if (period === "year") {
        apiStartYear = startYear;
        apiStartMonth = 1;
        apiEndYear = endYear;
        apiEndMonth = 12;
      } else {
        apiStartYear = startYear;
        apiStartMonth = startMonth;
        apiEndYear = endYear;
        apiEndMonth = endMonth;
      }

      const promise = getAveragePricePerParcelPerCourier(
        apiStartYear,
        apiStartMonth,
        apiEndYear,
        apiEndMonth
      );
      if (
        setSequenceStep != null &&
        sequenceStepIndex != null &&
        sequenceStep === sequenceStepIndex
      ) {
        Promise.resolve(promise).finally(() =>
          setSequenceStep(sequenceStepIndex + 1)
        );
      }
    }, [
      period,
      startYear,
      startMonth,
      startQuarter,
      endYear,
      endMonth,
      endQuarter,
      getAveragePricePerParcelPerCourier,
      sequenceStep,
      setSequenceStep,
      sequenceStepIndex,
    ]);

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "average_price_per_parcel.png";
        link.click();
      });
    };

    const downloadCsv = () => {
      setIsCsvDownloading(true);
      const params: Record<string, string> = {
        startYear: startYear.toString(),
        endYear: endYear.toString(),
        period: "daily",
      };
      params.startMonth =
        period === "year" ? "1" : period === "quarter" ? String((startQuarter - 1) * 3 + 1) : String(startMonth);
      params.endMonth =
        period === "year" ? "12" : period === "quarter" ? String(endQuarter * 3) : String(endMonth);

      axios
        .get("/parcels/stats/average-price-per-parcel-per-courier", { params })
        .then((response) => {
          const series = (response.data as typeof transformedData) ?? [];
          const allTs = new Set<number>();
          series.forEach((s) => {
            const d = (s.data as [number, number][] | undefined) ?? [];
            d.forEach(([ts]) => allTs.add(ts));
          });
          const sortedTs = Array.from(allTs).sort((a, b) => a - b);
          const header =
            "Date," +
            series
              .map((s) => String(s?.name ?? "").replace(/,/g, " "))
              .join(",") +
            "\n";
          const rows = sortedTs.map((ts) => {
            const values = series.map((s) => {
              const pair = ((s.data as [number, number][]) ?? []).find(([t]) => t === ts);
              const val = pair != null ? (pair[1] ?? 0) : 0;
              return (val / 100).toFixed(2);
            });
            return [dayjs(ts).format("YYYY-MM-DD"), ...values].join(",");
          });
          const csv = header + rows.join("\n");
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `average_price_per_parcel_daily_${dayjs().format("YYYY-MM-DD")}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Error downloading CSV:", error);
        })
        .finally(() => {
          setIsCsvDownloading(false);
        });
    };

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col w-full items-start">
          <div className="flex flex-col sm:flex-row w-full items-start justify-between gap-4 ">
            <div></div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap w-full min-w-0 items-start sm:items-center justify-start sm:justify-end gap-2 sm:gap-3">
              <div className="flex items-center gap-1 sm:gap-2">
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                      {period === "month"
                        ? "Monthly"
                        : period === "quarter"
                          ? "Quarterly"
                          : "Yearly"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    <MenuItem
                      disabled={period === "month"}
                      onClick={() => setPeriod("month")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Monthly
                    </MenuItem>
                    <MenuItem
                      disabled={period === "quarter"}
                      onClick={() => setPeriod("quarter")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Quarterly
                    </MenuItem>
                    <MenuItem
                      disabled={period === "year"}
                      onClick={() => setPeriod("year")}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      Yearly
                    </MenuItem>
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {startYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={startYear === y}
                        onClick={() => setStartYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                {period === "month" && (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {startMonthOptions.find((m) => m.value === startMonth)
                          ?.label || "Month"}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {startMonthOptions.map((m) => (
                        <MenuItem
                          key={m.value}
                          disabled={startMonth === m.value}
                          onClick={() => setStartMonth(m.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {m.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
                {period === "quarter" && (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                        {
                          quarterOptions.find((q) => q.value === startQuarter)
                            ?.label
                        }
                        <ChevronDown className="ml-1 sm:ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {quarterOptions.map((q) => (
                        <MenuItem
                          key={q.value}
                          disabled={startQuarter === q.value}
                          onClick={() => setStartQuarter(q.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {q.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {endYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {yearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={endYear === y}
                        onClick={() => setEndYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                {period === "month" && (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                        {endMonthOptions.find((m) => m.value === endMonth)
                          ?.label || "Month"}
                        <ChevronDown className="ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {endMonthOptions.map((m) => (
                        <MenuItem
                          key={m.value}
                          disabled={endMonth === m.value}
                          onClick={() => setEndMonth(m.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {m.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
                {period === "quarter" && (
                  <Menu>
                    <MenuHandler>
                      <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                        {
                          quarterOptions.find((q) => q.value === endQuarter)
                            ?.label
                        }
                        <ChevronDown className="ml-1 sm:ml-2" />
                      </button>
                    </MenuHandler>
                    <MenuList placeholder={undefined}>
                      {quarterOptions.map((q) => (
                        <MenuItem
                          key={q.value}
                          disabled={endQuarter === q.value}
                          onClick={() => setEndQuarter(q.value)}
                          className="font-satoshi text-base text-dark"
                          placeholder={undefined}
                        >
                          {q.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
              </div>

              <PrimaryButton
                size="sm"
                onClick={downloadCsv}
                disabled={!transformedData?.length || isCsvDownloading}
                label={isCsvDownloading ? "..." : "Download CSV"}
                className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
              />
              <PrimaryButton
                size="sm"
                onClick={downloadScreenshot}
                label="Download image"
                className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
              />
            </div>
          </div>
        </div>
        <section ref={snapshotRef} className="mt-4">
          <div className="flex items-center shrink-0">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Average price per parcel per courier (€)
            </p>
            <Tooltip
              content="avg. price = parcel revenue per courier / parcel volumes per courier. Not affected by location/city filters."
              placeholder={undefined}
            >
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>
          {isLoadingAveragePricePerParcel ? (
            <div className="flex flex-col items-center justify-center gap-2 h-[300px] w-full">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          ) : isAveragePricePerParcelError ? (
            <div className="flex items-center justify-center h-[300px] w-full">
              <p className="text-red-500 font-satoshi">
                Failed to load data. Please try again.
              </p>
            </div>
          ) : transformedData && transformedData.length > 0 ? (
            <Chart
              options={{
                ...avgPriceChartOptions,
                legend: { show: true, position: "right" as const },
                colors:
                  seriesColors.length > 0
                    ? seriesColors
                    : avgPriceChartOptions.colors,
                xaxis: {
                  tickAmount: monthlyTickAmount,
                  labels: {
                    formatter: (value: string) => {
                      const ts = Number(value);
                      if (period === "year") return dayjs(ts).format("YYYY");
                      if (period === "quarter") {
                        const d = dayjs(ts);
                        const q = Math.ceil((d.month() + 1) / 3);
                        return `Q${q} ${d.year()}`;
                      }
                      return dayjs(ts).format("MMM YYYY");
                    },
                  },
                },
                tooltip: {
                  ...avgPriceChartOptions.tooltip,
                  custom: function ({
                    series,
                    seriesIndex,
                    dataPointIndex,
                    w,
                  }: any) {
                    const seriesData = series.map(
                      (seriesValues: any, index: number) => ({
                        name: w.globals.seriesNames[index],
                        value: seriesValues[dataPointIndex],
                        color: w.config.colors[index],
                      })
                    );
                    seriesData.sort((a: any, b: any) => b.value - a.value);
                    const ts = w.globals.seriesX[seriesIndex][dataPointIndex];
                    let dateStr = dayjs(ts).format("MMM YYYY");
                    if (period === "year") dateStr = dayjs(ts).format("YYYY");
                    if (period === "quarter") {
                      const d = dayjs(ts);
                      dateStr = `Q${Math.ceil((d.month() + 1) / 3)} ${d.year()}`;
                    }
                    let tooltipHtml =
                      '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
                    tooltipHtml += `${dateStr}</div>`;
                    seriesData.forEach((item: any) => {
                      const formattedValue = new Intl.NumberFormat("nl-NL", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(Number(item.value ?? 0) / 100);
                      tooltipHtml += `
                        <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
                          <span class="apexcharts-tooltip-marker" style="background-color: ${item.color};"></span>
                          <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                            <div class="apexcharts-tooltip-y-group">
                              <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                              <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
                            </div>
                          </div>
                        </div>
                      `;
                    });
                    return tooltipHtml;
                  },
                },
              }}
              series={transformedData}
              type="line"
              height={300}
            />
          ) : null}
        </section>
      </div>
    );
  }
);

const avgPriceChartOptions: ApexOptions = {
  chart: {
    type: "line",
    zoom: { enabled: false },
    toolbar: { show: false },
  },
  dataLabels: { enabled: false },
  grid: { strokeDashArray: 5 },
  markers: { size: 1 },
  legend: { show: false },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
      const seriesData = series.map((seriesValues: any, index: number) => ({
        name: w.globals.seriesNames[index],
        value: seriesValues[dataPointIndex],
        color: w.config.colors[index],
      }));
      seriesData.sort((a: any, b: any) => b.value - a.value);

      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
      tooltipHtml += `${dayjs(
        w.globals.seriesX[seriesIndex][dataPointIndex]
      ).format("MMM YYYY")}</div>`;

      seriesData.forEach((item: any) => {
        const formattedValue = new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(item.value ?? 0) / 100);

        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${item.color};"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
              </div>
            </div>
          </div>
        `;
      });
      return tooltipHtml;
    },
  },
  yaxis: {
    title: {
      text: "Price per parcel (€)",
      style: { fontSize: "14px", fontWeight: "bold" },
    },
    labels: {
      formatter: (value: number) =>
        new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value ?? 0) / 100),
    },
  },
  colors: [
    "#23C16B",
    "#FFB323",
    "#48A7F8",
    "#FF3333",
    "#FF8800",
    "#9259FF",
    "#FF4DA6",
    "#33FFE6",
    "#808080",
    "#D17A50",
    "#FF7F50",
    "#8A2BE2",
    "#5F9EA0",
    "#DC143C",
    "#00FFFF",
    "#FF1493",
    "#696969",
    "#B8860B",
    "#FF4500",
    "#32CD32",
    "#4682B4",
    "#00FA9A",
    "#FFD700",
    "#FF6347",
    "#7B68EE",
    "#008080",
    "#6A5ACD",
    "#D2691E",
    "#B22222",
    "#9ACD32",
    "#8B008B",
    "#00CED1",
    "#FF69B4",
    "#1E90FF",
    "#40E0D0",
    "#EE82EE",
  ],
};
